import {
	isFunction,
	padStart,
	split,
	map
} from "lodash";
import { getBeTimeDiff } from "modules/selectors";
import { IGlobalStore } from "modules/types";
import moment from 'moment';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import styled, { css } from "styled-components";


const MS_IN_SECOND = 1000;
const SEC_IN_MINUTE = 60;
const MIN_IN_HOUR = 60;
const HRS_IN_DAY = 24;

const StyledElem = styled.div``;

interface ITimerPart {
	index: number;
}

const TimerPart = styled.div<ITimerPart>`
	width: 40%;
	${props => (props.index < 1) ? css`
		text-align: right;
	` : css`
		text-align: left;
	`}
`;

const defaultTimerValue = {
	days   : 0,
	hours  : 0,
	minutes: 0,
	seconds: 0,
	diff   : 0
};

interface IProps {
	format?: string;
	noRender?: boolean;
	onComplete?: () => void;
	onTick?: (time_to_end: number) => void;
	date: number | string | Date,
	be_time_diff: number;
	correction?: boolean;
	is_animated?: boolean;
	as?: string;
	separator?: React.ReactNode
	separator_key?: string
	withPadStart?: boolean
}

export interface IState {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
	diff: number;
	completed: boolean;
}

class TimerComponent extends React.Component<IProps, IState> {
	public static defaultProps = {
		format: 'hh:mm:ss',
		correction: true,
		withPadStart: true
	};
	private is_mounted = false;
	public state = {
		...defaultTimerValue,
		completed: false
	};

	private timer?: ReturnType<typeof setInterval>;

	constructor(props: IProps) {
		super(props);

		this.state = {
			...this.state,
			...this.timerValues(this.duration)
		};
	}

	private get duration() {
		const {be_time_diff, correction} = this.props;
		const startDate = moment(Date.now() + ( correction ? be_time_diff : 0 ));
		const endDate = moment(this.props.date);

		return endDate.diff(startDate);
	}

	private get base_timer() {
		const {diff, minutes, seconds, hours, days} = this.state;
		const {format = '', separator, separator_key = ':', withPadStart = true} = this.props;

		if ( !diff ) {
			return '00';
		}

		const padStartValue = withPadStart ? 2 : 0;
		const secs = padStart(String(seconds), padStartValue, '0');
		const mins = padStart(String(minutes), padStartValue, '0');
		const hrs = padStart(String(hours), padStartValue, '0');
		const ds = padStart(String(days), padStartValue, '0');

		const replaceFn = (str: string) =>
			str.replace('ss', secs)
				.replace('mm', mins)
				.replace('hh', hrs)
				.replace('dd', ds);

		if ( separator ) {
			const format_array = split(format, separator_key);

			const value = format_array.map(replaceFn);

			return (
				<Fragment>
					{map(value, (time: any, index: number) => (
						<Fragment key={`${time} ${index}`}>
							<TimerPart index={index}>{time}</TimerPart>
							{/* eslint-disable-next-line sonarjs/no-gratuitous-expressions */}
							{index + 1 < format_array.length && separator}
						</Fragment>
					))}
				</Fragment>
			);
		}

		return replaceFn(format);
	}

	public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.log(error);
	}

	private get animated_timer() {
		const {diff, seconds, minutes = 0} = this.state;

		if ( !diff ) {
			return '';
		}

		return seconds + minutes * 60;
	}

	/**
	 * @ignore
	 */
	public render() {

		const {is_animated, as, noRender} = this.props;

		if ( noRender ) {
			return null
		}
		const value = this.base_timer;

		if ( is_animated ) {
			const animated_value = this.animated_timer;

			return as ? (
				<StyledElem key={animated_value} as={as as any}>{animated_value}</StyledElem>
			) : animated_value;
		}

		return as ? (
			<StyledElem as={as as any}>{value}</StyledElem>
		) : value;
	}

	/**
	 * @ignore
	 */
	public componentDidMount() {
		if ( this.duration <= 0 ) {
			return;
		}

		this.timer = setInterval(
			() => this.diff(),
			333
		);

		this.is_mounted = true;
	}

	/**
	 * @ignore
	 */
	public componentWillUnmount() {
		this.is_mounted = false;
		this.clearTimer();
	}

	private clearTimer(): void {
		if (this.timer) {
			clearInterval(this.timer);
		}
	}

	private timerValues(diff: number) {
		if ( diff < MS_IN_SECOND ) {
			return defaultTimerValue;
		}

		const seconds = Math.floor(diff / MS_IN_SECOND);
		const minutes = Math.floor(seconds / SEC_IN_MINUTE);
		const hours = Math.floor(minutes / MIN_IN_HOUR);

		return {
			diff,
			days   : Math.floor(hours / HRS_IN_DAY),
			hours  : hours % HRS_IN_DAY,
			minutes: minutes % MIN_IN_HOUR,
			seconds: seconds % SEC_IN_MINUTE
		};
	}

	private diff() {

		const diff = this.duration;
		const {onTick} = this.props;

		if ( isFunction(onTick) ) {
			onTick(diff);
		}

		if ( this.is_mounted ) {
			if ( diff < MS_IN_SECOND ) {
				return this.handleCompleteState();
			}

			this.setState(this.timerValues(diff));
		} else {
			this.clearTimer();
		}

	}

	private handleCompleteState() {
		this.clearTimer();

		const {onComplete} = this.props;

		this.setState({
			completed: true,
			...defaultTimerValue
		});

		if ( isFunction(onComplete) ) {
			onComplete();
		}
	}
}

const mapStateToProps = (state: IGlobalStore) => ( {
	be_time_diff: getBeTimeDiff(state)
} );

export const Timer = connect(
	mapStateToProps
)(TimerComponent);
